import React from "react"

const DueTimeIndicator = ({ dueAt, urgency, submission, isSubmitted }) => {
  const formatDateShort = (dateStr) => {
    if (!dateStr) return null
    const date = new Date(dateStr)
    const options = {
      month: "short",
      day: "numeric",
    }
    return date.toLocaleDateString("en-US", options)
  }

  const formatDateWithTime = (dateStr) => {
    if (!dateStr) return null
    const date = new Date(dateStr)
    const options = {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }
    return date.toLocaleDateString("en-US", options)
  }

  const formatDateFull = (dateStr) => {
    if (!dateStr) return null
    const date = new Date(dateStr)
    const options = {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }
    return date.toLocaleDateString("en-US", options)
  }

  // For submitted/graded assignments, show actual dates instead of relative time
  if (isSubmitted) {
    const submittedAt = submission?.submitted_at
    const gradedAt = submission?.graded_at

    return (
      <span className="flex items-center gap-3 text-[var(--ink-light)]">
        {dueAt && (
          <span className="flex items-center gap-1.5" title={formatDateFull(dueAt)}>
            <svg className="w-3.5 h-3.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Due {formatDateShort(dueAt)}</span>
          </span>
        )}
        {submittedAt && (
          <span className="flex items-center gap-1.5 text-emerald-600" title={formatDateFull(submittedAt)}>
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Submitted {formatDateShort(submittedAt)}</span>
          </span>
        )}
        {!submittedAt && gradedAt && (
          <span className="flex items-center gap-1.5 text-emerald-600" title={formatDateFull(gradedAt)}>
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Graded {formatDateShort(gradedAt)}</span>
          </span>
        )}
      </span>
    )
  }

  if (!dueAt) {
    return (
      <span className="text-[var(--ink-light)] flex items-center gap-1.5">
        <svg className="w-3.5 h-3.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>No due date</span>
      </span>
    )
  }

  const date = new Date(dueAt)
  const now = new Date()

  const formatRelative = () => {
    const diffMs = date - now
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)

    // Calculate calendar day difference (not raw time difference)
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const dueMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const calendarDayDiff = Math.round((dueMidnight - todayMidnight) / 86400000)

    if (diffMs < 0) {
      const absDiffMins = Math.abs(diffMins)
      const absDiffHours = Math.abs(diffHours)
      const absCalendarDays = Math.abs(calendarDayDiff)

      if (absDiffMins < 60) return `${absDiffMins}m overdue`
      if (absDiffHours < 24) return `${absDiffHours}h overdue`
      return `${absCalendarDays}d overdue`
    }

    if (diffMins < 60) return `in ${diffMins}m`
    if (diffHours < 24) return `in ${diffHours}h`
    if (calendarDayDiff === 1) return "Tomorrow"
    if (calendarDayDiff < 7) return `in ${calendarDayDiff} days`
    return formatDateFull(dueAt)
  }

  const urgencyConfig = {
    overdue: {
      text: "text-red-600",
      icon: "text-red-500",
    },
    today: {
      text: "text-amber-600",
      icon: "text-amber-500",
    },
    tomorrow: {
      text: "text-orange-600",
      icon: "text-orange-500",
    },
    this_week: {
      text: "text-blue-600",
      icon: "text-blue-500",
    },
    later: {
      text: "text-[var(--ink-light)]",
      icon: "text-[var(--ink-light)]",
    },
  }

  const config = urgencyConfig[urgency] || urgencyConfig.later
  const relativeText = formatRelative()
  const actualDateTime = formatDateWithTime(dueAt)

  // Check if relative text already includes the full date (for items > 7 days out)
  const showActualTime = relativeText !== formatDateFull(dueAt)

  return (
    <span className={`flex items-center gap-1.5 font-medium ${config.text}`}>
      <svg className={`w-3.5 h-3.5 flex-shrink-0 ${config.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        {urgency === 'overdue' ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        )}
      </svg>
      <span className="flex flex-wrap items-center gap-x-1.5" title={formatDateFull(dueAt)}>
        <span>{relativeText}</span>
        {showActualTime && (
          <span className="text-[var(--ink-light)] font-normal">({actualDateTime})</span>
        )}
      </span>
    </span>
  )
}

export default DueTimeIndicator
